import { useEffect, useMemo, useState } from 'react';
import type { ITaskStore } from '../stores/ITaskStore';
import type { TaskSnapshot } from '../stores/types';
import {
  buildPredecessorDisplayText,
  buildSuccessorDisplayText,
  formatDependencyFieldLabel,
  resolveTaskWbsCode,
} from '../gantt/taskLinkDisplayUtils';

function resolveLocationNameFromTree(
  tree: any[] | null | undefined,
  locationId: string | null | undefined
): string {
  if (!tree || !locationId) return '';
  const target = String(locationId);
  const stack = [...tree];
  while (stack.length) {
    const node: any = stack.shift();
    if (String(node?.location_id ?? '') === target) {
      return String(node?.name ?? '').trim();
    }
    if (Array.isArray(node?.child) && node.child.length > 0) {
      stack.push(...node.child);
    }
  }
  return '';
}

export interface TaskDetailsSelection {
  selectedTaskId: string | null;
  task: TaskSnapshot | null;
  isLeafTask: boolean;
  readOnlyForNonLeaf: boolean;
  resolvedLocationValue: string;
  predecessorText: string;
  successorText: string;
  predecessorFieldLabel: string;
  successorFieldLabel: string;
}

/**
 * Selected task + derived read-only fields for task details UI.
 * Avoids scanning all tasks on each render (O(links) + O(1) leaf check only).
 */
export function useTaskDetailsSelection(
  store: ITaskStore,
  options?: { skipSyncWhileUpdating?: boolean }
): TaskDetailsSelection {
  const skipSync = options?.skipSyncWhileUpdating ?? false;

  const [selectedTaskId, setSelectedTaskId] = useState<string | null>(
    () => store.getSelectedTaskId()
  );
  const [task, setTask] = useState<TaskSnapshot | null>(() => {
    const id = store.getSelectedTaskId();
    return id ? store.getTask(id) ?? null : null;
  });

  useEffect(() => {
    let prevId = store.getSelectedTaskId();

    const syncFromStore = () => {
      const id = store.getSelectedTaskId();
      const idChanged = prevId !== id;
      prevId = id;

      // Always follow selection changes (e.g. clicking another grid row while details is open).
      // Only skip content refresh for the same task while the draft is dirty/saving.
      if (!idChanged && skipSync) {
        return;
      }

      setSelectedTaskId((prev) => (prev === id ? prev : id));
      if (!id) {
        setTask((prev) => (prev === null ? prev : null));
        return;
      }
      const next = store.getTask(id) ?? null;
      setTask((prev) => (prev === next ? prev : next));
    };

    syncFromStore();
    return store.subscribe(syncFromStore);
  }, [store, skipSync]);

  const locationTree = store.getLocationTree();
  const linksRevision = store.getLinksRevision();

  const derived = useMemo((): Omit<TaskDetailsSelection, 'selectedTaskId' | 'task'> => {
    if (!task) {
      return {
        isLeafTask: true,
        readOnlyForNonLeaf: false,
        resolvedLocationValue: '',
        predecessorText: '',
        successorText: '',
        predecessorFieldLabel: 'Predecessor',
        successorFieldLabel: 'Successor',
      };
    }

    const isLeafTask = !store.taskHasChildren(task.id);
    const links = store.getLinks();
    const taskId = String(task.id);

    const predecessorLinks = links.filter((link) => String(link.target) === taskId);
    const successorLinks = links.filter((link) => String(link.source) === taskId);

    const predecessorText = buildPredecessorDisplayText(taskId, links, (id) => store.getTask(id) ?? null);

    const successorText = buildSuccessorDisplayText(taskId, links, (id) => store.getTask(id) ?? null);

    const predecessorFieldLabel = formatDependencyFieldLabel(
      'Predecessor',
      predecessorLinks.map((link) => resolveTaskWbsCode(store.getTask(String(link.source)))),
    );

    const successorFieldLabel = formatDependencyFieldLabel(
      'Successor',
      successorLinks.map((link) => resolveTaskWbsCode(store.getTask(String(link.target)))),
    );

    const resolvedLocationValue =
      task.locationType?.trim() ||
      resolveLocationNameFromTree(locationTree, task.locationId ?? null) ||
      task.locationId ||
      '';

    return {
      isLeafTask,
      readOnlyForNonLeaf: !isLeafTask,
      resolvedLocationValue,
      predecessorText,
      successorText,
      predecessorFieldLabel,
      successorFieldLabel,
    };
  }, [task, store, locationTree, linksRevision]);

  return {
    selectedTaskId,
    task,
    ...derived,
  };
}
